import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { CitableFaq } from "../components/CitableFaq";
import { landingFaq } from "../content/geo-content";
import { usePlausible } from "@/hooks/usePlausible";
import { usePageMeta } from "@/hooks/usePageMeta";

const REGULATIONS = [
  {
    badge: "AFIR",
    label: "Alt-Fuels Infrastructure Regulation",
    description: "EU-weite Mindestanforderungen fuer Ladeinfrastruktur und Interoperabilitaet",
  },
  {
    badge: "Eichrecht (MessEG)",
    label: "Mess- und Eichgesetz",
    description: "Gesetzliche Grundlage fuer eichrechtskonforme Abrechnung an Ladepunkten",
  },
  {
    badge: "Eichrecht (MessEV)",
    label: "Mess- und Eichverordnung",
    description: "Technische Anforderungen an Messgeraete und Kalibrierung",
  },
  {
    badge: "LSV",
    label: "Ladesaeulenverordnung",
    description: "Deutsche Anforderungen an oeffentliche Ladeinfrastruktur und Abrechnung",
  },
  {
    badge: "BDEW",
    label: "BDEW-Leitfaden",
    description: "Branchenstandards des Bundesverbands der Energie- und Wasserwirtschaft",
  },
  {
    badge: "EnWG",
    label: "Energiewirtschaftsgesetz",
    description: "Rechtlicher Rahmen fuer Energieversorgung und Netzanschluss in Deutschland",
  },
  {
    badge: "CRA",
    label: "Cyber Resilience Act",
    description: "EU-Cybersicherheitsanforderungen fuer vernetzte Produkte und Infrastruktur",
  },
];

const PAIN_POINTS = [
  {
    title: "Regulierungskomplexitaet",
    body: "AFIR, Eichrecht, LSV, BDEW, EnWG und CRA haben jeweils eigene Anforderungen, Fristen und Strafrahmen — ein unuebersichtliches Geflecht aus Pflichten.",
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
      </svg>
    ),
  },
  {
    title: "Strafen & Bussgelder",
    body: "Nichtkonformitaet riskiert Geldbussen von bis zu 100.000 EUR und den Betriebsstopp Ihrer Ladeinfrastruktur — ein existenzielles Risiko fuer jeden CPO.",
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
    ),
  },
  {
    title: "Veraltete Methoden",
    body: "Tabellenkalkulationen und externe Berater (ab 3.500 EUR/Monat) kommen mit dem Tempo der Regulierungsaenderungen schlicht nicht mehr mit.",
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
      </svg>
    ),
  },
];

const FEATURES = [
  {
    title: "KI-Compliance-Chat",
    description:
      "Stellen Sie jede regulatorische Frage. Erhalten Sie Antworten mit Zitaten aus konkreten Paragraphen, Konfidenzwerten und unmittelbaren Handlungsempfehlungen.",
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
      </svg>
    ),
  },
  {
    title: "Lueckenanalyse",
    description:
      "Laden Sie Ihr Betreiberprofil hoch. Erhalten Sie einen Compliance-Score ueber alle 188 Anforderungen — mit priorisierten Luecken und konkreten Massnahmen.",
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z" />
      </svg>
    ),
  },
  {
    title: "Fristen-Alerts",
    description:
      "Verpassen Sie keine Deadline mehr. Automatische Benachrichtigungen mit Klassifizierung nach Dringlichkeit: ueberfaellig, dringend, Warnung, bevorstehend.",
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
      </svg>
    ),
  },
];

type ResourceStatus = "idle" | "submitting" | "success" | "error";

async function submitResourceRequest(resourceEmail: string, source: string): Promise<void> {
  const res = await fetch("/api/audit-request", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email: resourceEmail.trim(), source }),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(data.error || "Anfrage konnte nicht gesendet werden.");
  }
}

function ResourceForm({ source, buttonColor }: { source: string; buttonColor: string }) {
  const [resEmail, setResEmail] = useState("");
  const [resStatus, setResStatus] = useState<ResourceStatus>("idle");
  const [resError, setResError] = useState("");

  async function handleResSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!resEmail.trim() || resStatus === "submitting") return;
    setResStatus("submitting");
    setResError("");
    try {
      await submitResourceRequest(resEmail, source);
      setResStatus("success");
      setResEmail("");
    } catch (err) {
      setResError(err instanceof Error ? err.message : "Netzwerkfehler.");
      setResStatus("error");
    }
  }

  if (resStatus === "success") {
    return (
      <p className="mt-6 text-sm font-medium" style={{ color: buttonColor }}>
        Zugeschickt! Bitte prüfen Sie Ihr Postfach.
      </p>
    );
  }

  return (
    <form onSubmit={handleResSubmit} className="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center">
      <label htmlFor={`res-email-${source}`} className="sr-only">E-Mail-Adresse</label>
      <input
        id={`res-email-${source}`}
        type="email"
        required
        disabled={resStatus === "submitting"}
        value={resEmail}
        onChange={(e) => setResEmail(e.target.value)}
        placeholder="ihre@email.de"
        className="flex-1 rounded-lg border border-[var(--color-border)] px-4 py-2.5 text-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)] focus:outline-none focus:ring-2 disabled:opacity-60"
        style={{ focusRingColor: buttonColor } as React.CSSProperties}
      />
      <button
        type="submit"
        disabled={resStatus === "submitting"}
        className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
        style={{ background: buttonColor }}
      >
        {resStatus === "submitting" ? "Wird gesendet…" : "Anfordern"}
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
        </svg>
      </button>
      {resStatus === "error" && (
        <p className="mt-1 text-xs text-red-600">{resError}</p>
      )}
    </form>
  );
}

export function Landing() {
  usePageMeta({
    title: "ChargeSuite — Compliance-Sicherheit für Ihre Ladeinfrastruktur",
    canonical: "/",
    description:
      "ChargeSuite — KI-gestützte Compliance-Plattform für EV-Ladeinfrastruktur in Deutschland. 188 regulatorische Anforderungen, 20 Fristen, 7 Verordnungen — automatisch geprüft.",
  });
  const plausible = usePlausible();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  async function handleAuditSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || status === "submitting") return;
    setStatus("submitting");
    setErrorMessage("");
    try {
      const res = await fetch("/api/audit-request", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: trimmed, source: "landing" }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        setErrorMessage(data.error || "Anfrage konnte nicht gesendet werden.");
        setStatus("error");
        return;
      }
      plausible("audit_form_submit", { props: { source: "landing" } });
      setStatus("success");
      setEmail("");
    } catch {
      setErrorMessage("Netzwerkfehler. Bitte erneut versuchen.");
      setStatus("error");
    }
  }

  return (
    <div className="min-h-screen bg-white font-sans text-[var(--color-text-primary)]">
      {/* ── Nav ── */}
      <header className="sticky top-0 z-50 border-b border-[var(--color-border)] bg-white/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <span className="text-xl font-bold tracking-tight" style={{ letterSpacing: "-0.02em" }}>
            <span style={{ color: "var(--color-navy)" }}>Charge</span><span style={{ color: "var(--color-primary)" }}>Suite</span>
          </span>
          <nav className="hidden items-center gap-6 text-sm font-medium text-[var(--color-text-secondary)] sm:flex">
            <a href="#problem" className="transition-colors hover:text-[var(--color-text-primary)]">
              Problem
            </a>
            <a href="#solution" className="transition-colors hover:text-[var(--color-text-primary)]">
              Lösung
            </a>
            <a href="#regulations" className="transition-colors hover:text-[var(--color-text-primary)]">
              Verordnungen
            </a>
            <a href="#resources" className="transition-colors hover:text-[var(--color-text-primary)]">
              Ressourcen
            </a>
            <Link to="/afir-compliance-software" className="transition-colors hover:text-[var(--color-text-primary)]">
              AFIR
            </Link>
            <Link to="/blog" className="transition-colors hover:text-[var(--color-text-primary)]">
              Blog
            </Link>
            <Link to="/faq" className="transition-colors hover:text-[var(--color-text-primary)]">
              FAQ
            </Link>
            <Link
              to="/pricing"
              className="transition-colors hover:text-[var(--color-text-primary)]"
            >
              Preise
            </Link>
          </nav>
          <a
            href="#audit"
            className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[var(--color-primary-dark)]"
          >
            Kostenloses Audit
          </a>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden px-6 py-24 sm:py-32" style={{ background: "var(--color-navy)" }}>
        {/* Subtle dot-grid overlay */}
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.12) 1px, transparent 0)",
            backgroundSize: "40px 40px",
          }}
          aria-hidden="true"
        />
        {/* Right-side glow */}
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-1/2 opacity-10"
          style={{ background: "radial-gradient(ellipse at right center, #8B2252, transparent 70%)" }}
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-4xl text-center">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-semibold uppercase tracking-widest"
               style={{ borderColor: "rgba(30,94,255,0.4)", background: "rgba(30,94,255,0.12)", color: "#4D7FFF" }}>
            🇩🇪 Made in Germany
          </div>
          <h1 className="text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl" style={{ letterSpacing: "-0.02em" }}>
            Compliance-Sicherheit für{" "}
            <span style={{ color: "var(--color-primary)" }}>
              Ihre Ladeinfrastruktur.
            </span>
          </h1>
          <p className="mt-6 text-lg font-medium sm:text-xl" style={{ color: "rgba(255,255,255,0.65)" }}>
            188 regulatorische Anforderungen. Automatisch geprüft. Paragrafengenau zitiert.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href="#audit"
              className="w-full rounded-xl px-8 py-3.5 text-base font-semibold text-white shadow-md transition-all hover:shadow-lg sm:w-auto"
              style={{ background: "var(--color-primary)" }}
              onMouseEnter={e => (e.currentTarget.style.background = "var(--color-primary-dark)")}
              onMouseLeave={e => (e.currentTarget.style.background = "var(--color-primary)")}
              onClick={() => plausible("cta_click", { props: { label: "hero-audit" } })}
            >
              Kostenloser Compliance-Audit
            </a>
            <Link
              to="/pricing"
              className="w-full rounded-xl px-8 py-3.5 text-base font-semibold shadow-sm transition-all sm:w-auto"
              style={{ border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.85)" }}
            >
              Preise ansehen
            </Link>
          </div>
        </div>
      </section>

      {/* ── Stats bar ── */}
      <section className="border-y border-[var(--color-border)] bg-[var(--color-surface-secondary)]">
        <div className="mx-auto grid max-w-6xl grid-cols-2 divide-x divide-[var(--color-border)] px-6 py-8 text-center sm:grid-cols-4">
          {[
            { stat: "€100.000", label: "AFIR-Bußgeld (max.)" },
            { stat: "188", label: "Regulatorische Anforderungen" },
            { stat: "7", label: "Verordnungen abgedeckt" },
            { stat: "20", label: "Rechtliche Fristen" },
          ].map(({ stat, label }) => (
            <div key={label} className="px-4 py-2">
              <p className="font-mono text-3xl font-normal" style={{ color: "var(--color-primary)" }}>{stat}</p>
              <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Trust bar ── */}
      <section className="border-b border-[var(--color-border)] bg-white px-6 py-4">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-8 gap-y-3">
          {[
            { icon: "🇩🇪", label: "Datenverarbeitung in Deutschland" },
            { icon: "✓", label: "DSGVO-konform" },
            { icon: "🏭", label: "Made in Germany" },
            { icon: "📄", label: "AVV auf Anfrage" },
            { icon: "§", label: "Paragrafengenau — verifizierbar im Bundesgesetzblatt" },
          ].map(({ icon, label }) => (
            <div key={label} className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
              <span className="text-base leading-none">{icon}</span>
              <span>{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Problem ── */}
      <section id="problem" className="px-6 py-20 sm:py-28">
        <div className="mx-auto max-w-6xl">
          <div className="mb-14 text-center">
            <h2 className="text-3xl font-bold tracking-tight text-[var(--color-text-primary)] sm:text-4xl">
              188 Anforderungen. 20 Fristen. 7 Verordnungen.
            </h2>
            <p className="mt-4 text-base text-[var(--color-text-secondary)]">
              EV-Ladeinfrastruktur in Deutschland zu betreiben bedeutet, ein komplexes Netz aus
              Regulierungen zu navigieren — mit stetig wachsendem Tempo.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-3">
            {PAIN_POINTS.map(({ title, body, icon }) => (
              <div
                key={title}
                className="rounded-2xl border border-[var(--color-border)] bg-white p-8 shadow-sm"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-red-50 text-red-500">
                  {icon}
                </div>
                <h3 className="mb-3 text-lg font-semibold text-[var(--color-text-primary)]">
                  {title}
                </h3>
                <p className="text-sm leading-relaxed text-[var(--color-text-secondary)]">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Solution ── */}
      <section
        id="solution"
        className="bg-gradient-to-b from-blue-50 to-white px-6 py-20 sm:py-28"
      >
        <div className="mx-auto max-w-6xl">
          <div className="mb-14 text-center">
            <h2 className="text-3xl font-bold tracking-tight text-[var(--color-text-primary)] sm:text-4xl">
              Compliance Brain: Ihr KI-Regulierungsexperte
            </h2>
            <p className="mt-4 text-base text-[var(--color-text-secondary)]">
              Ein einziges System, das saemtliche Regulierungen kennt, Ihre Luecken erkennt
              und Sie proaktiv auf Fristen hinweist.
            </p>
          </div>
          <div className="grid gap-8 sm:grid-cols-3">
            {FEATURES.map(({ title, description, icon }) => (
              <div
                key={title}
                className="flex flex-col rounded-2xl border border-[var(--color-border)] bg-white p-8 shadow-sm"
              >
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--color-primary)] text-white">
                  {icon}
                </div>
                <h3 className="mb-3 text-lg font-semibold text-[var(--color-text-primary)]">
                  {title}
                </h3>
                <p className="text-sm leading-relaxed text-[var(--color-text-secondary)]">
                  {description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Regulations covered ── */}
      <section id="regulations" className="border-t border-[var(--color-border)] px-6 py-20 sm:py-28">
        <div className="mx-auto max-w-6xl">
          <div className="mb-14 text-center">
            <h2 className="text-3xl font-bold tracking-tight text-[var(--color-text-primary)] sm:text-4xl">
              Abgedeckte Verordnungen
            </h2>
            <p className="mt-4 text-base text-[var(--color-text-secondary)]">
              ChargeSuite kennt alle relevanten deutschen und EU-Vorschriften fuer CPOs.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {REGULATIONS.map(({ badge, label, description }) => (
              <div
                key={badge}
                className="rounded-xl border border-[var(--color-border)] bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
              >
                <span className="inline-block rounded-md bg-[var(--color-primary)] px-2.5 py-1 text-xs font-bold text-white">
                  {badge}
                </span>
                <p className="mt-3 text-sm font-semibold text-[var(--color-text-primary)]">
                  {label}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-[var(--color-text-muted)]">
                  {description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* -- GEO FAQ -- */}
      <section id="geo-faq" className="border-t border-[var(--color-border)] px-6 py-20 sm:py-28">
        <div className="mx-auto max-w-4xl">
          <div className="mb-10 text-center">
            <h2 className="text-3xl font-bold tracking-tight text-[var(--color-text-primary)] sm:text-4xl">
              Haeufige Compliance-Fragen
            </h2>
            <p className="mt-4 text-base text-[var(--color-text-secondary)]">
              Konkrete Antworten fuer Betreiber oeffentlich zugaenglicher Ladepunkte.
            </p>
          </div>
          <CitableFaq items={landingFaq} />
        </div>
      </section>

      {/* ── Free Resources ── */}
      <section id="resources" className="bg-gradient-to-b from-white to-[var(--color-surface-secondary)] px-6 py-20 sm:py-28">
        <div className="mx-auto max-w-6xl">
          <div className="mb-14 text-center">
            <h2 className="text-3xl font-bold tracking-tight text-[var(--color-text-primary)] sm:text-4xl">
              Kostenlose Compliance-Ressourcen
            </h2>
            <p className="mt-4 text-base text-[var(--color-text-secondary)]">
              Starten Sie mit konkretem Wissen — ohne Kosten, ohne Verpflichtung.
            </p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2">
            {/* AFIR Checklist */}
            <div className="rounded-2xl border border-[var(--color-border)] bg-white p-8 shadow-sm transition-shadow hover:shadow-md">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl text-white" style={{ background: "var(--color-primary)" }}>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z" />
                </svg>
              </div>
              <h3 className="mb-2 text-lg font-semibold text-[var(--color-text-primary)]">
                AFIR Compliance Checklist
              </h3>
              <p className="mb-4 text-sm leading-relaxed text-[var(--color-text-secondary)]">
                Alle 26 AFIR-Anforderungen fuer Ladepunktbetreiber auf einen Blick — mit
                Artikelverweisen, Kritikalitaetsstufen und Abhakmoeglickeit.
              </p>
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700">
                  14 kritisch
                </span>
                <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
                  9 hoch
                </span>
              </div>
              <ResourceForm source="resource-afir-checklist" buttonColor="var(--color-primary)" />
            </div>

            {/* Regulatory Calendar */}
            <div className="rounded-2xl border border-[var(--color-border)] bg-white p-8 shadow-sm transition-shadow hover:shadow-md">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl text-white" style={{ background: "#B8860B" }}>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                </svg>
              </div>
              <h3 className="mb-2 text-lg font-semibold text-[var(--color-text-primary)]">
                Regulatorischer Kalender 2025–2030
              </h3>
              <p className="mb-4 text-sm leading-relaxed text-[var(--color-text-secondary)]">
                20 regulatorische Fristen aus 7 Verordnungen — chronologisch sortiert, farblich
                kodiert nach Verordnung und Dringlichkeit.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                {["AFIR", "Eichrecht", "LSV", "EnWG", "CRA"].map((reg) => (
                  <span key={reg} className="rounded-md bg-[var(--color-surface-secondary)] px-2 py-0.5 text-xs font-medium text-[var(--color-text-secondary)]">
                    {reg}
                  </span>
                ))}
              </div>
              <ResourceForm source="resource-regulatory-calendar" buttonColor="#B8860B" />
            </div>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {[
              { to: "/afir-compliance-software", title: "AFIR Compliance Software" },
              { to: "/eichrecht-compliance", title: "Eichrecht Compliance" },
              { to: "/ladeinfrastruktur-compliance", title: "Ladeinfrastruktur Compliance" },
            ].map(({ to, title }) => (
              <Link
                key={to}
                to={to}
                className="rounded-xl border border-[var(--color-border)] bg-white p-5 text-sm font-semibold text-[var(--color-text-primary)] shadow-sm transition-shadow hover:shadow-md"
              >
                {title}
                <span className="mt-2 block text-xs font-normal leading-relaxed text-[var(--color-text-secondary)]">
                  Fachseite mit Betreiberpflichten, Nachweisen und ChargeSuite-Einsatzbereich.
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── Trueline ── */}
      <section className="px-6 py-16" style={{ background: "var(--color-navy)" }}>
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-2xl font-medium italic text-white sm:text-3xl" style={{ lineHeight: 1.4 }}>
            „Wir kennen jeden Paragraphen.
          </p>
          <p className="text-2xl font-medium italic sm:text-3xl" style={{ lineHeight: 1.4, color: "var(--color-primary)" }}>
            Sie kennen Ihre Ladepunkte."
          </p>
        </div>
      </section>

      {/* ── CTA ── */}
      <section
        id="audit"
        className="bg-[var(--color-primary)] px-6 py-20 sm:py-28"
      >
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Starten Sie mit einem kostenlosen Compliance-Audit
          </h2>
          <p className="mt-5 text-base leading-relaxed text-blue-100">
            In 30 Minuten zeigen wir Ihnen genau, wo Ihre Ladeinfrastruktur steht — mit
            KI-gestuetzter Analyse Ihrer regulatorischen Luecken.
          </p>
          <form
            onSubmit={handleAuditSubmit}
            className="mt-10 flex flex-col items-center gap-3 sm:flex-row sm:justify-center"
          >
            <label htmlFor="audit-email" className="sr-only">
              E-Mail-Adresse
            </label>
            <input
              id="audit-email"
              type="email"
              required
              disabled={status === "submitting" || status === "success"}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ihre@email.de"
              className="w-full rounded-xl border border-blue-300 bg-white px-5 py-3.5 text-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)] shadow-sm focus:outline-none focus:ring-2 focus:ring-white disabled:opacity-60 sm:w-80"
            />
            <button
              type="submit"
              disabled={status === "submitting" || status === "success"}
              className="w-full rounded-xl bg-white px-7 py-3.5 text-sm font-semibold text-[var(--color-primary)] shadow-sm transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {status === "submitting"
                ? "Wird gesendet..."
                : status === "success"
                  ? "Anfrage gesendet!"
                  : "Audit buchen"}
            </button>
          </form>
          {status === "success" && (
            <p className="mt-4 text-sm font-medium text-white" role="status">
              Vielen Dank — Sie erhalten binnen weniger Minuten eine Bestaetigung per E-Mail.
            </p>
          )}
          {status === "error" && (
            <p className="mt-4 text-sm font-medium text-amber-200" role="alert">
              {errorMessage}
            </p>
          )}
          <p className="mt-5 text-sm text-blue-200">
            Oder kontaktieren Sie uns direkt:{" "}
            <a
              href="mailto:contact@chargesuite.de"
              className="font-medium text-white underline decoration-blue-300 hover:decoration-white"
            >
              contact@chargesuite.de
            </a>
          </p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-[var(--color-border)] bg-white px-6 py-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 sm:flex-row">
          <span className="text-base font-bold" style={{ letterSpacing: "-0.02em" }}>
            <span style={{ color: "var(--color-navy)" }}>Charge</span><span style={{ color: "var(--color-primary)" }}>Suite</span>
          </span>
          <nav className="flex flex-wrap items-center justify-center gap-6 text-sm text-[var(--color-text-secondary)]">
            <Link to="/blog" className="transition-colors hover:text-[var(--color-text-primary)]">
              Blog
            </Link>
            <Link to="/faq" className="transition-colors hover:text-[var(--color-text-primary)]">
              FAQ
            </Link>
            <Link to="/afir-compliance-software" className="transition-colors hover:text-[var(--color-text-primary)]">
              AFIR
            </Link>
            <Link to="/eichrecht-compliance" className="transition-colors hover:text-[var(--color-text-primary)]">
              Eichrecht
            </Link>
            <Link to="/impressum" className="transition-colors hover:text-[var(--color-text-primary)]">
              Impressum
            </Link>
            <Link to="/privacy" className="transition-colors hover:text-[var(--color-text-primary)]">
              Datenschutz
            </Link>
            <Link to="/terms" className="transition-colors hover:text-[var(--color-text-primary)]">
              AGB
            </Link>
            <Link to="/pricing" className="transition-colors hover:text-[var(--color-text-primary)]">
              Preise
            </Link>
          </nav>
          <p className="text-sm text-[var(--color-text-muted)]">
            ChargeSuite 2026. Made in Germany.
          </p>
        </div>
      </footer>
    </div>
  );
}
